import { IconNames } from '@blueprintjs/icons';
import { DialogWithForm } from '@strata/react-dialog';
import { FullscreenLoading } from '@strata/react-fullscreen-loading';
import { TextInputFormik } from '@strata/react-inputs';
import { SuccessToast } from '@strata/react-toast-message';
import { validationStrings } from '@strata/strata-ui-strings';
import { Formik } from 'formik';
import * as Yup from 'yup';

import { handleError } from 'services/Error';
import { ClaimFrequencyTypes, ClaimSubmissionTypes } from 'services/MedicalBillingConstants';
import { useAppDispatch } from 'store';
import { type ClaimDetails } from 'store/api/medbillingApi';
import { type ReferralDetails } from 'store/api/treatmentApi';
import { submitClaim } from 'store/claims/claimDetailsThunks';

interface VoidClaimDialogProps {
    isOpen: boolean;
    onClose: () => void;
    claimDetails?: ClaimDetails;
    referralDetails?: ReferralDetails;
}

export const VoidClaimDialog = ({
    isOpen,
    onClose,
    claimDetails = undefined,
    referralDetails = undefined,
}: VoidClaimDialogProps) => {
    const dispatch = useAppDispatch();
    return isOpen ? (
        <Formik
            initialValues={{
                originalClaimNumber: claimDetails?.originalClaimNumber,
            }}
            validateOnMount
            enableReinitialize
            validationSchema={Yup.object({
                originalClaimNumber: Yup.string().required(
                    validationStrings.requiredField.errorMessage,
                ),
            })}
            onSubmit={async (values) => {
                try {
                    if (claimDetails && referralDetails) {
                        const claim = {
                            ...claimDetails,
                            originalClaimNumber: values.originalClaimNumber,
                            submissionType: ClaimSubmissionTypes.ADJUDICATE,
                            claimFrequencyType: ClaimFrequencyTypes.VOID,
                            friendlyId: referralDetails.friendlyId,
                            payerId: referralDetails.payerId,
                        };

                        await dispatch(submitClaim(claim));
                        SuccessToast({ message: 'Claim was voided successfully.' });
                        onClose();
                    }
                } catch (err) {
                    handleError('An error occurred voiding claim. Please try again.', err as Error);
                }
            }}
        >
            {(formik) => (
                <>
                    <FullscreenLoading
                        isShowing={formik.isSubmitting}
                        loadingText="Voiding claim..."
                    />
                    <DialogWithForm
                        dialogTitle="Void Claim"
                        headerIcon={IconNames.WARNING_SIGN}
                        primaryButtonLabel="Yes, Void Claim"
                        secondaryButtonLabel="No, Return To Form"
                        isOpen={isOpen}
                        onSecondaryButtonClick={onClose}
                        isPrimaryButtonDisabled={!formik.isValid || formik.isSubmitting}
                        dialogContent={
                            <>
                                <p className="margin__bottom--large">
                                    Are you sure you want to void this claim?
                                </p>
                                <TextInputFormik
                                    id="originalClaimNumber"
                                    label="Original Payer Claim ID"
                                    required
                                />
                            </>
                        }
                    />
                </>
            )}
        </Formik>
    ) : null;
};
